import { useCallback, useEffect, useRef, useState } from "react";
import { useAnalytics } from "../lib/analytics";
import { allQuestions } from "../lib/onboarding/questionDefinitions";
import * as OnboardingService from "../services/onboardingService";
import type {
	OnboardingAnswers,
	Question,
	UserData,
} from "../types/onboarding";

export const useOnboardingLogic = (userData: UserData | null) => {
	const [currentScreenIndex, setCurrentScreenIndex] = useState(0);
	const [answers, setAnswers] = useState<OnboardingAnswers>({});
	const [screens, setScreens] = useState<Question[][]>([]);
	const { logEvent } = useAnalytics();
	const hasInitialized = useRef(false);

	useEffect(() => {
		if (!userData || hasInitialized.current) return;

		hasInitialized.current = true;

		if (!userData.isOnboarded) {
			let questionsToConsider = allQuestions;

			if (userData.userType === "b2b") {
				questionsToConsider = [];
			} else if (
				userData.userType === "b2c" ||
				userData.userType === "partner"
			) {
				questionsToConsider = allQuestions.filter(
					(question) => question.type !== "profile-select",
				);
			}

			const missingQuestions = questionsToConsider.filter((question) => {
				if (question.fields && question.fields.length > 0) {
					// Special handling for setup-profile question
					// Only show if firstName is missing (location is optional)
					if (question.type === "setup-profile") {
						const firstNameMissing =
							!userData.firstName ||
							userData.firstName === null ||
							userData.firstName === undefined ||
							userData.firstName === "";

						// Show setup-profile only if name is missing (location is optional)
						return firstNameMissing;
					}

					// Special handling for setup-profile-details question
					// Show if yearOfBirth OR gender are missing
					if (question.type === "setup-profile-details") {
						const yearMissing =
							!userData.yearOfBirth ||
							userData.yearOfBirth === null ||
							userData.yearOfBirth === undefined;
						const genderMissing =
							!userData.gender ||
							userData.gender === null ||
							userData.gender === undefined ||
							userData.gender === "";

						// Show setup-profile-details if either year or gender is missing
						return yearMissing || genderMissing;
					}

					// Special handling for fertility-status question
					if (question.type === "fertility-status") {
						const statusMissing =
							!userData.fertilityStatus ||
							userData.fertilityStatus === null ||
							userData.fertilityStatus === undefined ||
							userData.fertilityStatus === "";
						return statusMissing;
					}

					// Special handling for fertility-role question
					if (question.type === "fertility-role") {
						const roleMissing =
							!userData.fertilityRole ||
							userData.fertilityRole === null ||
							userData.fertilityRole === undefined ||
							userData.fertilityRole === "";
						return roleMissing;
					}

					// Special handling for fertility-journey-types question
					if (question.type === "fertility-journey-types") {
						const journeyTypesMissing =
							!userData.fertilityJourneyTypes ||
							userData.fertilityJourneyTypes === null ||
							userData.fertilityJourneyTypes === undefined ||
							(Array.isArray(userData.fertilityJourneyTypes) &&
								userData.fertilityJourneyTypes.length === 0);
						return journeyTypesMissing;
					}

					// Special handling for needs-most question
					if (question.type === "needs-most") {
						const needsMostMissing =
							!userData.needsMost ||
							userData.needsMost === null ||
							userData.needsMost === undefined ||
							(Array.isArray(userData.needsMost) &&
								userData.needsMost.length === 0);
						return needsMostMissing;
					}

					// Special handling for how-did-you-hear question
					if (question.type === "how-did-you-hear") {
						const howDidYouHearMissing =
							!userData.howDidYouHearAboutUs ||
							userData.howDidYouHearAboutUs === null ||
							userData.howDidYouHearAboutUs === undefined ||
							(Array.isArray(userData.howDidYouHearAboutUs) &&
								userData.howDidYouHearAboutUs.length === 0);
						return howDidYouHearMissing;
					}

					// Special handling for additional-info question
					if (question.type === "additional-info") {
						const additionalInfoMissing =
							!userData.additionalInformation ||
							userData.additionalInformation === null ||
							userData.additionalInformation === undefined ||
							userData.additionalInformation === "";
						return additionalInfoMissing;
					}

					return question.fields.some((field) => {
						if (field === "lastName" || field === "phoneNumber") {
							return false;
						}
						const value = userData[field];
						return value === null || value === undefined || value === "";
					});
				}

				const value = userData[question.field];
				return value === null || value === undefined || value === "";
			});

			const sortedMissingQuestions = missingQuestions.sort((a, b) => {
				// Setup profile (combines userType, name, location) should always be first
				if (a.type === "setup-profile") return -1;
				if (b.type === "setup-profile") return 1;
				// Setup profile details (year, gender) should be second
				if (a.type === "setup-profile-details") return -1;
				if (b.type === "setup-profile-details") return 1;
				// Fertility status should be third
				if (a.type === "fertility-status") return -1;
				if (b.type === "fertility-status") return 1;
				// Fertility role should be fourth
				if (a.type === "fertility-role") return -1;
				if (b.type === "fertility-role") return 1;
				// Fertility journey types should be fifth
				if (a.type === "fertility-journey-types") return -1;
				if (b.type === "fertility-journey-types") return 1;
				// Needs most should be sixth
				if (a.type === "needs-most") return -1;
				if (b.type === "needs-most") return 1;
				// How did you hear should be seventh
				if (a.type === "how-did-you-hear") return -1;
				if (b.type === "how-did-you-hear") return 1;
				// Additional info should be eighth (last before thank you)
				if (a.type === "additional-info") return -1;
				if (b.type === "additional-info") return 1;
				// Legacy userType question (if it still exists) should be first
				if (a.field === "userType") return -1;
				if (b.field === "userType") return 1;
				return 0;
			});

			const generatedScreens = sortedMissingQuestions.map((question) => [
				question,
			]);
			setScreens(generatedScreens);

			if (generatedScreens.length > 0) {
				const missingFields = missingQuestions.map((q) => q.field);
				logEvent("onboarding_wizard_started", undefined, {
					total_questions: generatedScreens.length.toString(),
					missing_fields: missingFields.join(","),
					is_onboarded: String(userData.isOnboarded),
				});
			}
		} else {
			setScreens([]);
			logEvent("onboarding_wizard_already_complete", undefined, {
				is_onboarded: String(userData.isOnboarded),
			});
		}

		const initialAnswers: OnboardingAnswers = {};
		for (const key of Object.keys(userData)) {
			if (
				userData[key] !== null &&
				userData[key] !== undefined &&
				userData[key] !== ""
			) {
				initialAnswers[key] = String(userData[key]);
			}
		}
		setAnswers(initialAnswers);
	}, [userData, logEvent]);

	const handleInputChange = useCallback(
		(field: string, value: string | number | string[]) => {
			setAnswers((prev) => ({
				...prev,
				[field]: Array.isArray(value) ? value : String(value),
			}));
		},
		[],
	);

	const saveCurrentAnswer = useCallback(async () => {
		if (screens.length === 0 || currentScreenIndex >= screens.length) return;

		const currentScreen = screens[currentScreenIndex];
		const question = currentScreen[0];

		if (question.fields && question.fields.length > 0) {
			const fieldsToSave: OnboardingAnswers = {};

			for (const field of question.fields) {
				const value = answers[field];
				if (value !== null && value !== undefined && value !== "") {
					fieldsToSave[field] = value;
				}
			}

			if (Object.keys(fieldsToSave).length > 0) {
				try {
					await OnboardingService.saveOnboardingFields(fieldsToSave);

					logEvent("onboarding_fields_batch_saved", undefined, {
						fields_saved: Object.keys(fieldsToSave).join(","),
						question_index: currentScreenIndex.toString(),
						field_type: question.type,
						fields_count: Object.keys(fieldsToSave).length.toString(),
					});
				} catch (error) {
					console.error("Error saving fields:", error);
					logEvent("onboarding_fields_batch_save_error", undefined, {
						fields_attempted: Object.keys(fieldsToSave).join(","),
						question_index: currentScreenIndex.toString(),
						error_message: error instanceof Error ? error.message : "unknown",
					});
					throw error;
				}
			}
		} else {
			const currentValue = answers[question.field];

			if (currentValue && currentValue !== "" && currentValue !== null) {
				try {
					await OnboardingService.saveOnboardingField(
						question.field,
						Array.isArray(currentValue)
							? currentValue
							: typeof currentValue === "boolean"
								? currentValue.toString()
								: currentValue,
					);

					logEvent("onboarding_field_saved", undefined, {
						field_name: question.field,
						question_index: currentScreenIndex.toString(),
						field_type: question.type,
						has_value: "true",
					});
				} catch (error) {
					console.error("Error saving field:", error);
					logEvent("onboarding_field_save_error", undefined, {
						field_name: question.field,
						question_index: currentScreenIndex.toString(),
						error_message: error instanceof Error ? error.message : "unknown",
					});
					throw error;
				}
			}
		}
	}, [screens, currentScreenIndex, answers, logEvent]);

	return {
		currentScreenIndex,
		setCurrentScreenIndex,
		answers,
		setAnswers,
		screens,
		handleInputChange,
		saveCurrentAnswer,
	};
};
